import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "@shared/lib/http/axiosBaseQuery";
import {
  DocumentStructure,
  DocumentStructureAdd,
  DocumentStructureEdit,
} from "@models/document-structure";

export const documentsStructureApi = createApi({
  reducerPath: "documentsStructureApi",
  baseQuery: axiosBaseQuery({ baseUrl: "/api/" }),
  tagTypes: ["documentsStructure"],
  endpoints: (b) => ({
    getDocumentsStructure: b.query<DocumentStructure[], void>({
      query: () => ({
        url: "documents-structure/search",
        method: "POST",
        data: {},
      }),
      providesTags: ["documentsStructure"],
      keepUnusedDataFor: Infinity,
    }),

    editDocumentStructure: b.mutation<DocumentStructure, DocumentStructureEdit>({
      query: (documentStructureToUpdate) => ({
        url: `documents-structure/${documentStructureToUpdate.id}`,
        method: "PATCH",
        data: documentStructureToUpdate,
      }),
      async onQueryStarted(documentStructureToUpdate, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          documentsStructureApi.util.updateQueryData("getDocumentsStructure", undefined, (draft) => {
            const row = draft.find((t) => t.id === documentStructureToUpdate.id);
            if (row) Object.assign(row, documentStructureToUpdate);
          }),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: (_res, _err, accountType) => [{ type: "documentsStructure", id: accountType.id }],
    }),

    addCustomDocumentStructure: b.mutation<DocumentStructure, DocumentStructureAdd>({
      query: (customDocumentStructure) => ({
        url: "documents-structure",
        method: "POST",
        data: customDocumentStructure,
      }),
      async onQueryStarted(customDocumentStructure, { dispatch, queryFulfilled }) {
        const temp: DocumentStructure = {
          ...customDocumentStructure,
          id: 99999,
          business_id: 99999,
          created_at: "",
          updated_at: "",
          show_form_array: [],
          field_table: customDocumentStructure.field_table ?? null,
          location: customDocumentStructure.location ?? null,
        };
        const patch = dispatch(
          documentsStructureApi.util.updateQueryData("getDocumentsStructure", undefined, (draft) => {
            draft.push(temp);
          }),
        );
        try {
          const { data: created } = await queryFulfilled;
          dispatch(
            documentsStructureApi.util.updateQueryData("getDocumentsStructure", undefined, (draft) => {
              const i = draft.findIndex((x) => x.id === temp.id);
              if (i >= 0) draft[i] = created;
            }),
          );
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ["documentsStructure"],
    }),
  }),
});

export const {
  useGetDocumentsStructureQuery,
  useEditDocumentStructureMutation,
  useAddCustomDocumentStructureMutation,
} = documentsStructureApi;
